import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Download, Calendar, CreditCard } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
  useSEO({ title: "My Cards", description: "View and download your AI-generated sports trading cards.", path: "/account" });
  const { user, token, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Don't redirect if we just came from OAuth callback (token might still be setting)
    if (!authLoading && !user && !localStorage.getItem("pa_token")) {
      navigate("/login", { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user || !token) return;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [cardsResp, ordersResp] = await Promise.all([
          axios.get(`${API}/user/cards`, { headers }),
          axios.get(`${API}/user/orders`, { headers }),
        ]);
        setCards(cardsResp.data.cards || []);
        setOrders(ordersResp.data.orders || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [user, token]);

  if (authLoading || (!user && !authLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-cream)]">
        <div className="w-10 h-10 border-3 border-[var(--color-gold)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-16" data-testid="account-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-heading text-4xl uppercase tracking-tight text-[var(--color-text-dark)]">
              My Cards
            </h1>
            <p className="font-body text-sm text-[var(--color-text-mid)]">{user?.email}</p>
          </div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="font-body text-sm text-[var(--color-text-light)] hover:text-[var(--color-text-dark)] transition-colors"
            data-testid="account-logout-btn"
          >
            Sign out
          </button>
        </div>

        {/* Cards Grid */}
        <section className="mb-12">
          <h2 className="font-heading text-2xl uppercase tracking-tight text-[var(--color-text-dark)] mb-6">
            Your Legends
          </h2>
          {loading ? (
            <div className="flex gap-4">
              {[1,2,3].map(i => (
                <div key={i} className="w-48 h-64 bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] animate-shimmer" />
              ))}
            </div>
          ) : cards.length === 0 ? (
            <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-10 text-center">
              <p className="font-body text-[var(--color-text-mid)] mb-4">No cards yet. Create your first legend!</p>
              <Link
                to="/create"
                className="inline-block font-heading tracking-widest uppercase px-6 py-3 rounded-[8px]"
                style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
                data-testid="create-first-card-btn"
              >
                Create Card
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {cards.map((card) => {
                const isAdmin = !!card.full_image_url;
                return (
                <div key={card.id} data-testid={`card-${card.id}`}>
                  <Link
                    to={card.status === "preview_ready" || card.status === "purchased" ? `/preview/${card.id}` : "#"}
                    className="trading-card-frame card-tilt cursor-pointer block"
                  >
                    {(card.full_image_url || card.preview_image_url) ? (
                      <img
                        src={card.full_image_url || card.preview_image_url}
                        alt={card.dog_name}
                        className="w-full aspect-[2/3] object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full aspect-[2/3] bg-[var(--color-cream)] flex items-center justify-center">
                        <span className="font-body text-xs text-[var(--color-text-light)]">{card.status}</span>
                      </div>
                    )}
                    <div className="p-3" style={{ background: "linear-gradient(transparent, var(--color-navy))" }}>
                      <p className="font-heading text-white text-sm tracking-wider uppercase">{card.dog_name}</p>
                      <p className="font-accent text-xs capitalize" style={{ color: "var(--color-gold)" }}>
                        {card.sport} &middot; {card.position}
                      </p>
                    </div>
                  </Link>
                  {isAdmin && card.full_image_url && (
                    <div className="flex gap-1 mt-1">
                      {["original", "wallpaper", "instagram"].map((fmt) => (
                        <button
                          key={fmt}
                          onClick={(e) => {
                            e.preventDefault();
                            const link = document.createElement("a");
                            link.href = `${API}/admin/download-card/${card.id}?format=${fmt}`;
                            link.download = `${card.dog_name}-${fmt}.png`;
                            // For cookie-based auth, just click directly
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className={`flex-1 text-center font-accent text-[10px] py-1 rounded ${fmt === "original" ? "bg-[var(--color-gold)] text-[var(--color-navy)]" : "bg-[var(--color-navy)] text-white"}`}
                        >
                          {fmt === "original" ? "HD" : fmt === "wallpaper" ? "9:16" : "1:1"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Order History */}
        <section>
          <h2 className="font-heading text-2xl uppercase tracking-tight text-[var(--color-text-dark)] mb-6">
            Order History
          </h2>
          {orders.length === 0 ? (
            <p className="font-body text-sm text-[var(--color-text-light)]">No orders yet.</p>
          ) : (
            <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] overflow-hidden">
              {orders.map((order, i) => (
                <div
                  key={order.id}
                  className={`flex items-center justify-between p-4 ${i < orders.length - 1 ? "border-b border-[var(--color-border)]" : ""}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(245,166,35,0.1)" }}>
                      <CreditCard size={14} style={{ color: "var(--color-gold)" }} />
                    </div>
                    <div>
                      <p className="font-accent text-xs text-[var(--color-text-dark)]">#{order.id?.slice(0, 8)}</p>
                      <p className="font-body text-xs text-[var(--color-text-light)] flex items-center gap-1">
                        <Calendar size={10} /> {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`font-accent text-xs px-2 py-0.5 rounded ${order.status === 'paid' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>
                      {order.status}
                    </span>
                    <span className="font-body text-sm text-[var(--color-text-dark)]">
                      ${(order.amount_total / 100).toFixed(2)}
                    </span>
                    {order.download_token && order.status === 'paid' && (
                      <div className="flex items-center gap-2">
                        <a href={`${API}/download/${order.download_token}?format=original`} download title="HD Card" className="text-[var(--color-gold)] hover:text-[var(--color-gold-dark)]" data-testid={`download-order-${order.id}`}>
                          <Download size={16} />
                        </a>
                        <a href={`${API}/download/${order.download_token}?format=wallpaper`} download title="Phone Wallpaper" className="text-[var(--color-text-light)] hover:text-[var(--color-gold)]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                        </a>
                        <a href={`${API}/download/${order.download_token}?format=instagram`} download title="Instagram Post" className="text-[var(--color-text-light)] hover:text-[var(--color-gold)]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/></svg>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
